import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";

import { Button } from "react-bootstrap";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";

import { fetchData } from "../utils/apiUtils";
import AnswerBox from "../components/AnswerBox";
import {
  faMale,
  faQuestionCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { calculateDistance } from "../utils/mapUtils";
import { useLang } from "../utils/LangContext";
import Layout from "../components/Layout";
import GameQRCodeModal from "../components/GameQrComponent";
import localStorageHelper from "../utils/localStorageHelper";
import config from "../config";

import useCheckAnswer from "../hooks/useCheckAnswer";

// Marker for player location
const PlayerLocationMarker = ({ position }) => {
  const icon = {
    url: `data:image/svg+xml;utf8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 448 512">
        <path d="${faMale.icon[4]}" fill="blue" />
      </svg>`
    )}`,
    scaledSize: { width: 40, height: 40 },
    anchor: { x: 20, y: 20 },
  };

  return <Marker position={position} icon={icon} />;
};

// Marker for question points
const QuestionMarker = ({
  position,
  color = "red",
  iconData = faQuestionCircle,
}) => {
  const icon = {
    url: `data:image/svg+xml;utf8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 512 512">
        <path d="${iconData.icon[4]}" fill="${color}" />
      </svg>`
    )}`,
    scaledSize: { width: 30, height: 30 },
    anchor: { x: 20, y: 20 },
  };

  return <Marker position={position} icon={icon} />;
};

const Game = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  const [playerLocation, setPlayerLocation] = useState({
    lat: 59.421393,
    lng: 24.798492,
  });
  const [center, setCenter] = useState({ lat: 59.421393, lng: 24.798492 });

  const [questions, setQuestions] = useState([]);
  const [showAnswerBox, setShowAnswerBox] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [distanceToPoint, setDistanceToPoint] = useState(null);

  const DEFAULT_RADIUS = 10;
  const [pointRadius, setPointRadius] = useState(DEFAULT_RADIUS);
  const GOOGLE_MAPS_API_KEY = config.googleApiKey;

  const team = localStorageHelper.getItem("team");
  const game = localStorageHelper.getItem("game");



  // Persist current question index across reloads
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    
    /*
    const saved = window.localStorage.getItem("currentQuestionIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
    */

    const savedIndex = window.localStorage.getItem("currentQuestionIndex");
    const savedHash = window.localStorage.getItem("currentGameHash");
    const savedTeam = window.localStorage.getItem("currentTeamCode");

    // Resume only if BOTH match
    if (
      savedIndex !== null &&
      savedHash === game?.hash &&
      savedTeam === team?.team_code
    ) {
      return parseInt(savedIndex, 10);
    }
    return 0;

  });

  // Whenever game and team changes, persist them
useEffect(() => {
  if (game?.hash && team?.team_code) {
    window.localStorage.setItem("currentGameHash", game.hash);
    window.localStorage.setItem("currentTeamCode", team.team_code);
  }
}, [game?.hash, team?.team_code]);



  useEffect(() => {
    window.localStorage.setItem("currentQuestionIndex", currentQuestionIndex);
  }, [currentQuestionIndex]);

  // Derive the current question once for reuse
  const currentQuestion = useMemo(
    () => questions[currentQuestionIndex] || null,
    [questions, currentQuestionIndex]
  );

  const mapOptions = {
    disableDefaultUI: false,
    gestureHandling: "greedy",
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    styles: [
      {
        featureType: "poi",
        elementType: "all",
        stylers: [{ visibility: "off" }],
      },
      // hide transit
      {
        featureType: "transit",
        elementType: "all",
        stylers: [{ visibility: "off" }],
      },
      // keep street labels visible
      {
        featureType: "road",
        elementType: "labels.text",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ visibility: "on" }],
      },
      // optional: dim landscape to make roads pop
      {
        featureType: "landscape",
        elementType: "all",
        stylers: [{ saturation: -20 }, { lightness: 20 }],
      },
    ],
  };

  // Load questions on mount
  useEffect(() => {
    const loadQuestions = async () => {
      const code = team?.team_code;
      if (!code) return;

      const data = await fetchData(`/api/v1/game/questions?team_code=${code}`);
      if (Array.isArray(data) && data.length > 0) setQuestions(data);
    };
    loadQuestions();
  }, [team?.team_code]);

  // Watch player location and update distance
  useEffect(() => {
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const newLocation = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setPlayerLocation(newLocation);

        if (!mapLoaded) {
          setCenter(newLocation);
          setMapLoaded(true);
        }

        if (currentQuestion) {
          const target = {
            lat: currentQuestion.relPlace.latitude,
            lng: currentQuestion.relPlace.longitude,
          };

          // Compute effective radius locally to avoid race with setState
          const rawRadius = currentQuestion?.relPlace?.radius;
          const effectiveRadius =
            typeof rawRadius === "number" && rawRadius > 0
              ? rawRadius
              : DEFAULT_RADIUS;

          const distance = calculateDistance(newLocation, target);
          setDistanceToPoint(distance);
          if (pointRadius !== effectiveRadius) setPointRadius(effectiveRadius);

          if (distance <= effectiveRadius && !showAnswerBox) {
            setShowAnswerBox(true);
          }
        }
      },
      (err) => console.error(err),
      { enableHighAccuracy: true }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [currentQuestion, mapLoaded, showAnswerBox, pointRadius]);


  // Recenter the map whenever the current question changes
  useEffect(() => {
    if (!currentQuestion) return;
    setCenter({
      lat: currentQuestion.relPlace.latitude,
      lng: currentQuestion.relPlace.longitude,
    });
  }, [currentQuestionIndex, currentQuestion]);

  // Handle answering question
  const handleAnswer = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      // Game over: clear saved index and navigate
      window.localStorage.removeItem("currentQuestionIndex");
      const game = localStorageHelper.getItem("game");
      navigate(`/leaderboard/${game.hash}`);
    }
    setShowAnswerBox(false);
    setDistanceToPoint(null);
  };

  const onLoad = (_mapInstance) => {};

  const handleNextQuestion = () => {
    if (currentQuestionIndex <= questions.length - 1) {
      setShowAnswerBox(true);
    } else {
      const game = localStorageHelper.getItem("game");
      navigate(`/leaderboard/${game.hash}`);
    }
  };

  // Build polling query for current question
  const query = currentQuestion
    ? `/api/v1/game/is-answered?team_code=${team?.team_code}&game_hash=${game?.hash}&question_id=${currentQuestion.relQuestion.id}`
    : null;

  const { startChecking, stopChecking } = useCheckAnswer(
    query,
    handleAnswer, // when answered, go to next question
    setShowAnswerBox // still controls showing modal
  );

    // Always (re)start checking whenever currentQuestion changes
  useEffect(() => {
    if (query) {
      startChecking();
      return () => stopChecking();
    }
  }, [query, startChecking, stopChecking]);

  return (
    <Layout>
      {!team?.team_code ? (
        <div>
          <h2 style={{ textAlign: "center", marginTop: "20px", color: "red" }}>
            {t("error.noteam_code")}
          </h2>
          <div className="mt-4">
            <Link to="/create-team" className="btn btn-primary mx-2">
              {t("Create New Team")}
            </Link>
            <Link to="/join-team" className="btn btn-secondary mx-2">
              {t("Join Team")}
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ width: "100%", height: "100%" }} className="d-flex flex-column">

          <h2 style={{ textAlign: "center" }}>
            {team.team_name} <GameQRCodeModal code={team.team_code} />
          </h2>
          <h3 style={{ textAlign: "center" }}>
            {t("Answered")}: {currentQuestionIndex}/{questions.length}
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.9)",
                zIndex: 1000,
                fontSize: "1rem",
              }}
            >
              {distanceToPoint !== null
                ? distanceToPoint <= pointRadius
                  ? t("You're on place!")
                  : `${distanceToPoint.toFixed(1)} ` + t("m to the point")
                : t("Distance...")}
            </div>
          </h3>

          {(team.team_member?.email === "ljudmila.pihel@gmail.com" ||
            team.team_member?.email === "enerboy@gmail.com" ||
            team.team_member?.email === "info@hurtlocker.pro") && (
            <Button
              onClick={handleNextQuestion}
              style={{ margin: "10px", padding: "10px" }}
            >
              Next Question
            </Button>
          )}

        <div style={{ flex: "1 1 auto" }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              defaultCenter={center}
              defaultZoom={16}
              style={{ height: "100%", width: "100%" }}
              options={mapOptions}
              onLoad={onLoad}
              onCenterChanged={(ev) => {
                // @vis.gl/react-google-maps sends the center in the event detail
                const c = ev?.detail?.center;
                if (c) setCenter(c);
              }}
            >
              {/* Show ONLY the current question marker */}
              {currentQuestion && (
                <QuestionMarker
                  key={currentQuestion.id}
                  position={{
                    lat: currentQuestion.relPlace.latitude,
                    lng: currentQuestion.relPlace.longitude,
                  }}
                  color="green"
                  iconData={faExclamationCircle}
                />
              )}

              <PlayerLocationMarker position={playerLocation} />
            </Map>

            {currentQuestion && showAnswerBox && (
              <AnswerBox
                currentQuestion={currentQuestion}
                onAnswerSubmitted={handleAnswer}
                setShowAnswerBox={setShowAnswerBox}
              />
            )}
          </APIProvider>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Game;
